import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle } from '@fortawesome/pro-light-svg-icons/faCheckCircle';
import { faExclamationCircle } from '@fortawesome/pro-solid-svg-icons/faExclamationCircle';

import React, { useState, useCallback, useEffect } from 'react';

import axios from 'axios';
import classnames from 'classnames';
import { FormattedMessage } from 'react-intl';

import { Button } from 'ui-components/components/button';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';
import { NotificationService, NOTIFICATION_TYPE_ERROR } from 'ui-components/services/notification-service';

import messages from './messages';
import style from './style.module.less';

export const IDPConsentPage = () => {
  const [acceptedConsent, setAcceptedConsent] = useState(false);
  const [rejectedConsent, setRejectedConsent] = useState(false);
  const [isLoading, setIsLoading] = useAsyncSafeState(false);
  const [scopes, setScopes] = useAsyncSafeState([]);
  const [clientName, setClientName] = useAsyncSafeState('');
  const [clientLogoURI, setClientLogoURI] = useAsyncSafeState('');

  const searchParams = new URLSearchParams(window.location.search);
  const challenge = searchParams.get('consent_challenge');

  const onClickAcceptConsent = useCallback(() => {
    setAcceptedConsent(true);
    const formData = new FormData();
    formData.append('consent_challenge', challenge);
    axios
      .post('/idp/consent', formData, {
        headers: {
          ContentType: 'multipart/form-data',
        },
      })
      .then(({ data }) => (window.location.href = data.redirectUrl))
      .catch(window.console.log);
  }, [challenge]);

  const onClickRejectConsent = useCallback(() => {
    setRejectedConsent(true);
    const formData = new FormData();
    formData.append('consent_challenge', challenge);
    axios
      .post('/idp/reject_consent', formData, {
        headers: {
          ContentType: 'multipart/form-data',
        },
      })
      .then(({ data }) => (window.location.href = data.redirectUrl))
      .catch(window.console.log);
  }, [challenge]);

  useEffect(() => {
    setIsLoading(true);
    axios
      .get(`/idp/consent?consentChallenge=${challenge}`)
      .then((res) => {
        setScopes(res.data.scopes);
        setClientName(res.data.clientName);
        setClientLogoURI(res.data.clientLogoURI);
        setIsLoading(false);
      })
      .catch(() => {
        NotificationService.showNotification({
          notificationType: NOTIFICATION_TYPE_ERROR,
          title: <FormattedMessage {...messages.fetchErrorNotificationTitle} />,
          content: <FormattedMessage {...messages.fetchErrorNotificationContent} />,
          duration: 3000,
        });
      });
  }, [setScopes, setIsLoading, setClientName, setClientLogoURI, challenge]);

  return (
    <div className={style['page-wrapper']}>
      <h1 className={style['page-title']}>
        {clientName}
        {clientLogoURI && <img alt='Client IDP Logo' className={style['client-logo']} src={clientLogoURI} />}
      </h1>
      <hr />
      {isLoading && <LoadingIndicator className={style['loading-indicator']} />}
      {!isLoading && !acceptedConsent && !rejectedConsent && (
        <>
          <p>
            <FormattedMessage {...messages.clientWantsAccess} values={{ clientName }} />
          </p>
          <ul className={style.list}>
            {scopes.map((scope) => (
              <li key={scope}>
                <p>
                  - <FormattedMessage {...messages[scope]} />
                </p>
              </li>
            ))}
          </ul>
          <p>
            <FormattedMessage {...messages.accordanceStatement} values={{ clientName }} />
          </p>
          <div>
            <Button className={style['action-button']} onClick={onClickAcceptConsent} theme='primary'>
              <FormattedMessage {...messages.acceptConsent} values={{ clientName }} />
            </Button>
            <Button className={style['action-button']} onClick={onClickRejectConsent} theme='tertiary'>
              <FormattedMessage {...messages.cancel} />
            </Button>
          </div>
        </>
      )}
      {!isLoading && (acceptedConsent || rejectedConsent) && (
        <div className={style['consent-redirect-wrapper']}>
          <div className={style['consent-text']}>
            {acceptedConsent && (
              <>
                <FontAwesomeIcon className={classnames(style['redirect-icon'], style.green)} icon={faCheckCircle} />
                <p className={style.green}>
                  <b>
                    <FormattedMessage {...messages.acceptedConsentText} values={{ clientName }} />
                  </b>
                </p>
              </>
            )}
            {rejectedConsent && (
              <>
                <FontAwesomeIcon className={classnames(style['redirect-icon'], style.red)} icon={faExclamationCircle} />
                <p className={style.red}>
                  <b>
                    <FormattedMessage {...messages.rejectedConsentText} values={{ clientName }} />
                  </b>
                </p>
              </>
            )}
            <p className={style.subtext}>
              <FormattedMessage {...messages.redirectConsentText} values={{ clientName }} />
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
